import React, { useState, useCallback, useEffect } from "react";
import { GridLayout, useContainerWidth, type Layout, type LayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

// ── types ─────────────────────────────────────────────────────────────────────

export interface WidgetDef {
  id: string;
  defaultLayout: { x: number; y: number; w: number; h: number };
  minW?: number;
  minH?: number;
  title: React.ReactNode;
  color?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

// ── constants ─────────────────────────────────────────────────────────────────

const ROW_HEIGHT = 28;
const COLS = 12;
const STORAGE_KEY = "sflow_dash_layout_v3";
const COLLAPSED_KEY = "sflow_dash_collapsed_v3";
const CLOSED_KEY = "sflow_dash_closed_v3";
const COLLAPSED_H = 1;

// ── helpers ───────────────────────────────────────────────────────────────────

function toLayoutItems(widgets: WidgetDef[]): LayoutItem[] {
  return widgets.map((w) => ({
    i: w.id,
    x: w.defaultLayout.x,
    y: w.defaultLayout.y,
    w: w.defaultLayout.w,
    h: w.defaultLayout.h,
    minW: w.minW ?? 2,
    minH: w.minH ?? 3,
  }));
}

function loadLayout(widgets: WidgetDef[]): LayoutItem[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const savedLayout = JSON.parse(saved) as LayoutItem[];
      const savedIds = new Set(savedLayout.map((l) => l.i));
      // Merge in any new widgets that weren't in the saved layout
      // (e.g. after a software update adds new dashboard widgets).
      const newItems = widgets
        .filter((w) => !savedIds.has(w.id))
        .map((w) => ({
          i: w.id,
          x: w.defaultLayout.x,
          y: w.defaultLayout.y,
          w: w.defaultLayout.w,
          h: w.defaultLayout.h,
          minW: w.minW ?? 2,
          minH: w.minH ?? 3,
        }));
      return [...savedLayout, ...newItems];
    }
  } catch {}
  return toLayoutItems(widgets);
}

function loadSet(key: string): Set<string> {
  try {
    const s = localStorage.getItem(key);
    if (s) return new Set(JSON.parse(s));
  } catch {}
  return new Set();
}

// ── inner grid (receives measured width) ──────────────────────────────────────

function Grid({
  width,
  widgets,
  layout,
  setLayout,
  collapsed,
  closed,
  onCollapse,
  onClose,
}: {
  width: number;
  widgets: WidgetDef[];
  layout: LayoutItem[];
  setLayout: (l: LayoutItem[]) => void;
  collapsed: Set<string>;
  closed: Set<string>;
  onCollapse: (id: string) => void;
  onClose: (id: string) => void;
}) {
  const visible = widgets.filter((w) => !closed.has(w.id));
  const visibleLayout: Layout = layout.filter((l) => !closed.has(l.i));

  return (
    <GridLayout
      width={width}
      layout={visibleLayout}
      gridConfig={{
        cols: COLS,
        rowHeight: ROW_HEIGHT,
        margin: [8, 8],
        containerPadding: [0, 0],
      }}
      dragConfig={{ enabled: true, handle: ".wg-drag-handle" }}
      resizeConfig={{ enabled: true, handles: ["se"] }}
      onLayoutChange={(newLayout: Layout) => {
        setLayout(
          layout.map((old) => {
            const updated = newLayout.find((n) => n.i === old.i);
            if (!updated) return old;
            return { ...old, x: updated.x, y: updated.y, w: updated.w, h: updated.h };
          })
        );
      }}
    >
      {visible.map((w) => {
        const isCollapsed = collapsed.has(w.id);
        return (
          <div key={w.id} className="wg-card" style={{ margin: 0, overflow: "hidden" }}>
            {/* header acts as drag handle */}
            <div
              className="wg-header wg-drag-handle"
              style={{ ["--wg-accent" as string]: w.color ?? "var(--cyan)" }}
            >
              {w.icon && (
                <span className="wg-header-icon" style={{ color: w.color }}>
                  {w.icon}
                </span>
              )}
              <span className="wg-header-title">{w.title}</span>
              <div
                className="wg-header-actions"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  className="wg-btn"
                  title={isCollapsed ? "Expand" : "Collapse"}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, cursor: "pointer" }}
                  onClick={() => onCollapse(w.id)}
                >
                  {isCollapsed ? "▼" : "▲"}
                </span>
                <span
                  className="wg-btn wg-btn-close"
                  title="Close"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, cursor: "pointer" }}
                  onClick={() => onClose(w.id)}
                >
                  ✕
                </span>
              </div>
            </div>

            {!isCollapsed && (
              <div style={{ height: `calc(100% - 28px)`, overflow: "auto" }}>
                {w.children}
              </div>
            )}
          </div>
        );
      })}
    </GridLayout>
  );
}

// ── main export ───────────────────────────────────────────────────────────────

export default function DashboardGrid({ widgets }: { widgets: WidgetDef[] }) {
  const [layout, setLayoutState] = useState<LayoutItem[]>(() => loadLayout(widgets));
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadSet(COLLAPSED_KEY));
  const [closed, setClosed] = useState<Set<string>>(() => loadSet(CLOSED_KEY));
  const [savedH, setSavedH] = useState<Record<string, number>>({});
  const { containerRef, width, mounted } = useContainerWidth();

  const setLayout = useCallback((l: LayoutItem[]) => {
    setLayoutState(l);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(l));
  }, []);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]));
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem(CLOSED_KEY, JSON.stringify([...closed]));
  }, [closed]);

  const onCollapse = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setLayoutState((lay) => {
          const updated = lay.map((item) =>
            item.i === id
              ? { ...item, h: savedH[id] ?? (item.minH ?? 8), minH: item.minH ?? 3 }
              : item
          );
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          return updated;
        });
      } else {
        next.add(id);
        setLayoutState((lay) => {
          const cur = lay.find((l) => l.i === id);
          if (cur) setSavedH((sh) => ({ ...sh, [id]: cur.h }));
          const updated = lay.map((item) =>
            item.i === id ? { ...item, h: COLLAPSED_H, minH: COLLAPSED_H } : item
          );
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          return updated;
        });
      }
      return next;
    });
  }, [savedH]);

  const onClose = useCallback((id: string) => {
    setClosed((prev) => new Set([...prev, id]));
  }, []);

  const resetLayout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(COLLAPSED_KEY);
    localStorage.removeItem(CLOSED_KEY);
    setLayoutState(toLayoutItems(widgets));
    setCollapsed(new Set());
    setClosed(new Set());
    setSavedH({});
  }, [widgets]);

  return (
    <div ref={containerRef as React.Ref<HTMLDivElement>}>
      {closed.size > 0 && (
        <div style={{ textAlign: "right", marginBottom: 6 }}>
          <span className="restore-widgets-link" onClick={resetLayout}>
            ↺ Restore all widgets
          </span>
        </div>
      )}
      {mounted && (
        <Grid
          width={width}
          widgets={widgets}
          layout={layout}
          setLayout={setLayout}
          collapsed={collapsed}
          closed={closed}
          onCollapse={onCollapse}
          onClose={onClose}
        />
      )}
    </div>
  );
}
